import {
  useGetAgencyPromptTemplatesQuery,
  useGetPromptTemplatesQuery,
} from '@saaga/shared/store/prompt-template-endpoints.store'
import { useMemo } from 'react'

import type { PromptTemplate } from '@/components/chat/prompt-template.interface'

const EMPTY_LIST: PromptTemplate[] = []
const DEFAULT_PAGE_SIZE = 100

interface UseChatTemplateListReturn {
  templateList: PromptTemplate[]
  isLoadingTemplates: boolean
}

/**
 * Fetches and merges tenant and agency prompt templates for chat input.
 * In agency context: agency templates + tenant templates (deduplicated).
 * In default context: tenant templates only.
 */
export const useChatTemplateList = (agencySlug?: string): UseChatTemplateListReturn => {
  const tenantQuery = useGetPromptTemplatesQuery({ page: 1, pageSize: DEFAULT_PAGE_SIZE })

  const agencyQuery = useGetAgencyPromptTemplatesQuery(
    agencySlug
      ? { agencySlug, page: 1, pageSize: DEFAULT_PAGE_SIZE }
      : { agencySlug: '', page: 1, pageSize: DEFAULT_PAGE_SIZE },
    { skip: !agencySlug }
  )

  const tenantList = tenantQuery.data?.itemList ?? EMPTY_LIST
  const agencyList = agencyQuery.data?.itemList ?? EMPTY_LIST
  const isLoadingTemplates = tenantQuery.isLoading || (!!agencySlug && agencyQuery.isLoading)

  const templateList = useMemo(() => {
    if (!agencySlug) return tenantList
    const agencyIdSet = new Set(agencyList.map((t) => t.id))
    const uniqueTenantList = tenantList.filter((t) => !agencyIdSet.has(t.id))
    return [...agencyList, ...uniqueTenantList]
  }, [agencySlug, tenantList, agencyList])

  return { templateList, isLoadingTemplates }
}
